import {Range} from 'notebook/tables/excess/FunctionConstants';
import * as util from 'notebook/tables/excess/Util';
import {EvaluablePrimitive} from 'notebook/tables/typed-values/DataTypes';
import {ArrayTypedValue, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';

import {getUniformRange} from './uniformRange';

/**
 * Generate the search and result arrays of EvaluablePrimitives/TypedValues for table lookup functions when given a search
 * and result range.
 * @param searchRange The 2D array of EvaluablePrimitives/TypedValues in the given search columns & rows
 * @param resultRange The 2D array of EvaluablePrimitives/TypedValues in the given result columns & rows. A null value
 * for resultRange is used to decide if we will use VLOOKUP or HLOOKUP (based on the shape of searchRange) behavior
 * for the LOOKUP function. Null should be used sparingly in other contexts as it causes confusing product behavior.
 * @returns The arrays of EvaluablePrimitives/TypedValues values in the given search and result ranges
 */
export default function createLookupLists<T extends EvaluablePrimitive | TypedValue>(
  searchRange: Range<T> | ArrayTypedValue,
  resultRange: Range<T> | ArrayTypedValue | null
): {
  searchList: Array<T>;
  resultList: Array<T>;
} {
  // Converge Ranges and ArrayTypedValues inputs to be Ranges by extracting the raw value for ArrayTypedValues
  const processRange = <T extends EvaluablePrimitive | TypedValue>(
    range: Range<T> | ArrayTypedValue | null
  ): Range<T> | null => {
    const uniformRange = getUniformRange(range);
    if (uniformRange != null) {
      const row = new Array<T>(uniformRange.columns).fill(uniformRange.element as T);
      return new Array<Array<T>>(uniformRange.rows).fill(row);
    }
    return (
      (range as ArrayTypedValue)?.rawValue
        ? (range as ArrayTypedValue).rawValue.map((array: ArrayTypedValue) =>
            array.rawValue.map((item) => item)
          )
        : range
    ) as Range<T>;
  };

  const processedSearchRange: Range<T> | null = processRange(searchRange);
  if (!processedSearchRange) {
    throw new util.NotApplicableError(`Lookup function was not provided a search range.`);
  }
  const processedResultRange: Range<T> | null = processRange(resultRange);

  const firstRow = (range: Range<T>): Array<T> => range[0];
  const firstCol = (range: Range<T>): Array<T> => Array.from(range).map((row) => row[0]);
  const lastRow = (range: Range<T>): Array<T> => range[range.length - 1];
  const lastCol = (range: Range<T>): Array<T> => Array.from(range).map((row) => row[row.length - 1]);

  function getList(paramName: string, range: Range<T>): Array<T> {
    if (range.length === 1) {
      return firstRow(range);
    } else if (range[0].length === 1) {
      return firstCol(range);
    } else {
      throw new util.NotApplicableError(
        `Lookup function "${paramName}" must be a single row or a single column.`
      );
    }
  }

  let resultList: Array<T>;
  let searchList: Array<T>;

  if (processedResultRange != null) {
    // Row/column for resultRange
    searchList = getList('search range', processedSearchRange);
    resultList = getList('result range', processedResultRange);
  } else if (processedSearchRange.length >= processedSearchRange[0].length) {
    // square or more rows, search first column
    searchList = firstCol(processedSearchRange);
    resultList = lastCol(processedSearchRange);
  } else {
    // otherwise, search first row
    searchList = firstRow(processedSearchRange);
    resultList = lastRow(processedSearchRange);
  }

  return {searchList, resultList};
}
